import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { parse } from 'cookie';
import snakeCaseKeys from 'snakecase-keys';
import camelCaseKeys from 'camelcase-keys';
import { ErrorCodes } from 'consts/enums';
import { FetchError } from 'utils/errors';
import { sendHttpErrorResponseLog } from 'utils/sentry';

export default function useFetch() {
  const [loading, setLoading] = useState(false);

  const request = useCallback(async (url, method, body, options = {}) => {
    setLoading(true);

    try {
      const { csrftoken } = parse(document.cookie);

      let bodyPayload = null;
      if (body) {
        const snakeCasedBody = snakeCaseKeys(body, { deep: true });
        bodyPayload = JSON.stringify(snakeCasedBody);
      }

      const response = await fetch(url, {
        method,
        body: bodyPayload,
        headers: new Headers({
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        }),
        ...options,
      });

      const data = await response.json();

      const json = camelCaseKeys(data, { deep: true });

      setLoading(false);

      if (json.errorCode !== ErrorCodes.Success) {
        sendHttpErrorResponseLog({
          path: url,
          method,
          requestBody: bodyPayload,
          responseBody: data,
        });

        throw new FetchError(json);
      }

      return json;
    } catch (e) {
      setLoading(false);

      if (!e.errorCode) {
        Sentry.captureException(new Error(`Error on API ${url}: ${e}`));
      }

      throw e;
    }
  }, []);

  return { request, loading };
}
